/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react'
import { scaleRadial, scaleBand } from 'd3-scale'
import { max } from 'd3-array'
import { arc, stack } from 'd3-shape'
import Color from '../../../Utilities/Colors/ColorMap.ts'
import Legend from '../../../Legend/Legend.tsx'
import PitchTypeToPitchName from '../../../Utilities/Mapping/PitchTypeToPitchName'

export default function Axis({ settings, chartOptions = {} }) {
    const innerRadius = chartOptions.innerRadius || 67
    const outerRadius = chartOptions.outerRadius || 220

    const pitchTypes = chartOptions.pitchTypes || [
        'infer_n_ff',
        'infer_n_si',
        'infer_n_fc',
        'infer_n_ch',
        'infer_n_sl',
        'infer_n_st',
        'infer_n_sv',
        'infer_n_cu',
        'infer_n_kc',
        'infer_n_fs',
        'infer_n_sc',
        'infer_n_fo',
    ]

    const spinData = settings.data

    const pitchTypeOrder = pitchTypes.map((d) => d.split('_')[2].toUpperCase())

    const usedPitchTypes = spinData
        .map((row) =>
            pitchTypes
                .map((type) => ({
                    type,
                    n: Number(row[type]),
                }))
                .filter((d) => d.n > 0)
                // This may not work someday...
                .map((d) => d.type.split('_')[2].toUpperCase())
        )
        .flat()

    const uniquePitchTypes = Array.from(new Set(usedPitchTypes))
        .map((d) => ({
            pitchType: d,
            // These are used by legend. We use the preFormatted prop to skip formatting/sorting
            event: d,
            eventFormatted: PitchTypeToPitchName(d).toUpperCase(),
        }))
        .sort((a, b) => {
            const aIndex = pitchTypeOrder.indexOf(a.pitchType)
            const bIndex = pitchTypeOrder.indexOf(b.pitchType)
            return aIndex - bIndex
        })

    const y = scaleRadial()
        .domain([0, max(spinData, (d) => Number(d.y))])
        .range([innerRadius, outerRadius])

    const x = scaleBand()
        .domain(spinData.map((d) => d.x))
        .range([0, 2 * Math.PI])
        .align(0)

    const fifteenMinutes = (2 * Math.PI) / 48

    const arcShape = arc()
        .innerRadius((d) => y(d[0]))
        .outerRadius((d) => y(d[1]))
        .startAngle((d) => x(d.data.x) - fifteenMinutes)
        .endAngle((d) => x(d.data.x) - fifteenMinutes + x.bandwidth())
        .padAngle(0.01)
        .padRadius(innerRadius)

    const initStacks = stack().keys(pitchTypes)(spinData)
    const [stacks, setStacks] = useState(initStacks)

    const wholeNumbers = spinData.filter(
        (d) => Math.round(Number(d.x)) === Number(d.x)
    )

    const texts = wholeNumbers.map((d) => {
        const angle = x(d.x) - Math.PI / 2

        const xVal = Math.cos(angle) * (innerRadius * 0.75)
        const yVal = Math.sin(angle) * (innerRadius * 0.75)

        return (
            <text
                key={d.x}
                x={xVal}
                y={yVal}
                textAnchor="middle"
                dominantBaseline="middle"
            >
                {d.x}
            </text>
        )
    })

    const ticks = wholeNumbers.map((d) => {
        const angle = ((x(d.x) - Math.PI / 2) * 180) / Math.PI
        const transform = `rotate(${angle})`

        return (
            <g key={d.x} transform={transform}>
                <line
                    x1={innerRadius - 4}
                    y1="0"
                    x2={innerRadius}
                    y2="0"
                    strokeWidth="2"
                    stroke="black"
                />
            </g>
        )
    })

    // reduce flicker by waiting a split second
    let mouseOver = false
    const handleMouseOver = useCallback(
        (e, { pitchType }) => {
            const filteredPitchType = pitchTypes.filter((d) =>
                d.includes(`_${pitchType.toLowerCase()}`)
            )
            const newStacks = stack().keys(filteredPitchType)(spinData)

            setStacks(newStacks)

            mouseOver = true
        },
        [setStacks]
    )

    const handleMouseOut = useCallback(() => {
        mouseOver = false
        setTimeout(() => {
            if (!mouseOver) {
                setStacks(initStacks)
            }
        }, 200)
    }, [setStacks])

    return (
        <>
            <g
                transform={`translate(${settings.vizWidth / 2},${
                    settings.vizWidth / 2
                })`}
                key="spinAxisChart"
                id="spinAxisChart"
            >
                <circle
                    r={innerRadius}
                    strokeWidth={chartOptions.strokeWidth || 0.75}
                    stroke={chartOptions.stroke || '#000'}
                    fill="none"
                />
                {stacks.map((layer, i) => {
                    const pitchType = String(
                        layer.key.split('_')[2]
                    ).toUpperCase()
                    const pitchColor = Color('pitchName', pitchType)
                    return (
                        <g
                            // eslint-disable-next-line react/no-array-index-key
                            key={`radial_${i}`}
                            fill={pitchColor}
                            opacity={chartOptions.opacity}
                        >
                            {layer.map((pitch, j) => (
                                <path
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`axis_path_${j}`}
                                    d={arcShape(pitch)}
                                    className={`arc-${pitchType}`}
                                    strokeWidth={
                                        chartOptions.strokeWidth || 0.75
                                    }
                                    stroke={chartOptions.stroke || '#000'}
                                    opacity={pitch[0] === pitch[1] ? 0 : 1}
                                />
                            ))}
                        </g>
                    )
                })}
                {texts}
                {ticks}
            </g>
            {chartOptions.legend ? (
                <Legend
                    settings={{
                        data: uniquePitchTypes,
                        vizWidth: settings.vizWidth,
                        vizScale: 1,
                    }}
                    handleMouseOver={handleMouseOver}
                    handleMouseOut={handleMouseOut}
                    preFormatted
                    type={chartOptions.legendType}
                />
            ) : null}
        </>
    )
}
